import { useMemo } from 'react';
import { MarketType } from '@drift-labs/sdk';
import useDriftClient from './useDriftClient';
import useCalculateTradeFee from './useCalculateTradeFee';
import { getIsZeroFeesActiveForMarket } from 'src/utils/zeroFees';
import Env from 'src/environmentVariables/EnvironmentVariables';

interface BaseMarketFeesProps {
	marketType: MarketType;
	marketIndex: number;
}

interface MarketFeesResult {
	takerFeePct: number;
	makerFeePct: number;
	isZeroFeesPromotion: boolean;
	actualTakerFeePct?: number; // For showing crossed out fees
	actualMakerFeePct?: number; // For showing crossed out fees
}

/**
 * Hook to get base market fees, falling back to direct SDK call when user-specific fees return 0%
 * This ensures market fee information is always available regardless of user connection status
 */
export const useBaseMarketFees = ({
	marketType,
	marketIndex,
}: BaseMarketFeesProps): MarketFeesResult => {
	const driftClient = useDriftClient();

	const marketFees = useCalculateTradeFee({
		quoteSize: 0,
		marketType,
		marketIndex,
	});

	const baseMarketFees = useMemo(() => {
		const isZeroFeesPromotion = getIsZeroFeesActiveForMarket({
			marketIndex,
			isPerp: marketType === MarketType.PERP,
		});

		// Get the actual market fees (either from hook or SDK)
		let actualTakerFeePct = marketFees.takerFeePct;
		let actualMakerFeePct = marketFees.makerFeePct;

		// For zero fee promotion markets, use generic base fees if SDK doesn't provide them
		if (isZeroFeesPromotion) {
			// Always use generic base fees for display in zero fee promotion markets
			actualTakerFeePct = Env.defaultTakerFeeBps / 100; // Convert basis points to percentage
			actualMakerFeePct = -Env.defaultMakerRebateBps / 100; // Convert basis points to percentage (negative for rebate)

			return {
				takerFeePct: 0,
				makerFeePct: 0,
				isZeroFeesPromotion: true,
				actualTakerFeePct,
				actualMakerFeePct,
			};
		}

		// For normal markets, only get from SDK if hook returns 0%
		if (marketFees.takerFeePct === 0 && marketFees.makerFeePct === 0) {
			// Get base market fees from driftClient without user account
			if (driftClient?.isSubscribed) {
				try {
					const fees = driftClient.getMarketFees(marketType, marketIndex);
					actualTakerFeePct = fees.takerFee * 100;
					actualMakerFeePct = fees.makerFee * 100;
				} catch (error) {
					console.warn('Failed to get base market fees:', error);
				}
			}
		}

		// For normal markets, return the actual fees
		return {
			takerFeePct: actualTakerFeePct,
			makerFeePct: actualMakerFeePct,
			isZeroFeesPromotion: false,
		};
	}, [marketFees, marketIndex, marketType, driftClient]);

	return baseMarketFees;
};
